import { zodResolver } from "@hookform/resolvers/zod";
import { OptionWithSideElements } from "@jutro/ui/dist/Select/tools";
import dayjs, { Dayjs } from "dayjs";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import {
  EdmGenreType,
  EdmGetAllDoctorsFromShiftsDocument,
  EdmGetAllShiftsDocument,
  EdmGetAllStaffWithGenreAndWorkingHoursDocument,
  EdmMedicalAppShiftWithoutVisitFragment,
  useEdmCreateShiftMutation,
  useEdmCreateShiftsBulkMutation,
  useEdmDeleteShiftMutation,
  useEdmGetVisitTypesByGenreQuery,
  useEdmRestoreShiftMutation,
} from "lib/graphql/megaSchema";
import { organizationClinicOption } from "lib/hooks/useVojevodshipOptions";
import { toaster } from "lib/tools/toaster";
import {
  ShiftFormSchemaType,
  SlotActionModalMode,
  StaffOption,
  formatTimeToDate,
  getNfzStatus,
  getShiftFormDefaultValues,
  shiftFormSchema,
} from "views/StaffSchedule/StaffScheduleCreator/tools";
import {
  staffIdsWithNurseVisitTypes,
  visitTypeLabelMap,
} from "views/StaffSchedule/tools";

type Props = {
  staffOptions?: OptionWithSideElements<StaffOption>[];
  selectedBlockStaffId: string;
  organizationsClinicsOptions: organizationClinicOption[];
  startDate: Dayjs;
  selectedOrganizationId: string;
  selectedShift: EdmMedicalAppShiftWithoutVisitFragment | undefined;
  selectedDay?: Dayjs;
  onCloseModal: () => void;
};

export const useShiftModalForm = ({
  organizationsClinicsOptions,
  startDate,
  selectedShift,
  staffOptions,
  selectedDay,
  selectedOrganizationId,
  selectedBlockStaffId,
  onCloseModal,
}: Props) => {
  const [createShift, { loading: createShiftLoading }] =
    useEdmCreateShiftMutation({
      refetchQueries: [
        EdmGetAllShiftsDocument,
        EdmGetAllDoctorsFromShiftsDocument,
        EdmGetAllStaffWithGenreAndWorkingHoursDocument,
      ],
    });

  const [createShiftsBulk, { loading: createShiftsBulkLoading }] =
    useEdmCreateShiftsBulkMutation({
      refetchQueries: [
        EdmGetAllShiftsDocument,
        EdmGetAllDoctorsFromShiftsDocument,
        EdmGetAllStaffWithGenreAndWorkingHoursDocument,
      ],
    });

  const [restoreShift, { loading: restoreShiftLoading }] =
    useEdmRestoreShiftMutation({
      refetchQueries: [
        EdmGetAllShiftsDocument,
        EdmGetAllStaffWithGenreAndWorkingHoursDocument,
      ],
    });

  const [deleteShift, { loading: deleteShiftLoading }] =
    useEdmDeleteShiftMutation({
      refetchQueries: [
        EdmGetAllShiftsDocument,
        EdmGetAllDoctorsFromShiftsDocument,
        EdmGetAllStaffWithGenreAndWorkingHoursDocument,
      ],
    });

  const loading =
    createShiftLoading ||
    createShiftsBulkLoading ||
    restoreShiftLoading ||
    deleteShiftLoading;

  const {
    control,
    formState: { errors },
    watch,
    handleSubmit,
    setValue,
    trigger,
  } = useForm<ShiftFormSchemaType>({
    mode: "onChange",
    defaultValues: getShiftFormDefaultValues(
      staffOptions,
      selectedBlockStaffId,
      organizationsClinicsOptions,
      selectedOrganizationId,
      selectedShift,
      selectedDay,
      startDate,
    ),
    resolver: zodResolver(shiftFormSchema),
  });

  const { staff } = watch();

  const staffGenre: EdmGenreType | null = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!staff?.value) {
      return null;
    }

    const { genre, npwz } = staff.value;

    if (genre === "NURSE" && npwz?.endsWith("A")) {
      return "MIDWIFE";
    }

    return genre;
  }, [staff]);

  const { data: visitTypesData } = useEdmGetVisitTypesByGenreQuery({
    variables: {
      input: { genre: staffGenre! },
    },
    skip: !staffGenre,
  });

  const { data: additionalNurseVisitTypesData } =
    useEdmGetVisitTypesByGenreQuery({
      variables: {
        input: { genre: "NURSE" },
      },
      skip: !staff || !staffIdsWithNurseVisitTypes.includes(staff.value.id),
    });

  const visitTypeOptions = useMemo(() => {
    if (!visitTypesData) {
      return [];
    }

    const baseOptions = visitTypesData.edmGetVisitTypesByGenre.map(
      (visitType) => {
        return {
          label: visitTypeLabelMap[visitType],
          value: visitType,
        };
      },
    );

    if (
      staffIdsWithNurseVisitTypes.includes(staff.value.id) &&
      additionalNurseVisitTypesData?.edmGetVisitTypesByGenre
    ) {
      const additionalNurseVisitTypeOptions =
        additionalNurseVisitTypesData.edmGetVisitTypesByGenre.map(
          (visitType) => {
            return {
              label: visitTypeLabelMap[visitType],
              value: visitType,
            };
          },
        );

      return [...baseOptions, ...additionalNurseVisitTypeOptions];
    }

    return baseOptions;
  }, [visitTypesData, additionalNurseVisitTypesData]);

  const onSubmit = async (
    formData: ShiftFormSchemaType,
    mode: SlotActionModalMode,
  ) => {
    if (mode === "delete") {
      if (!selectedShift) {
        return;
      }

      const deleteShiftPromise = deleteShift({
        variables: {
          id: selectedShift.id,
        },
      });

      await toaster.promise(deleteShiftPromise, {
        success: () => {
          onCloseModal();
          return "Poprawnie usunięto zmianę";
        },
        loading: "Usuwam zmianę...",
        error: "Nie udało się usunąć zmiany",
      });
      return;
    }

    if (mode === "cancelDelete") {
      if (!selectedShift) {
        return;
      }

      const restoreShiftPromise = restoreShift({
        variables: {
          id: selectedShift.id,
        },
      });

      await toaster.promise(restoreShiftPromise, {
        success: () => {
          onCloseModal();
          return "Poprawnie przywrócono zmianę";
        },
        loading: "Przywracam zmianę...",
        error: "Nie udało się przywrócić zmiany",
      });

      return;
    }

    const {
      changeRepeating,
      organizationId,
      staff,
      visitTypes,
      workPlace,
      acceptingNfz,
      acceptingPriv,
      availableDays,
      dateStartFrom,
      dateEnded,
      timeFrom,
      timeTo,
      comment,
      isCustomVisitDuration,
      overrideStaffVisitDuration,
    } = formData;

    const extractedVisitTypes = visitTypes.map((visitType) => visitType.value);

    if (
      staff.value.acceptChildren &&
      extractedVisitTypes.includes("IN_PERSON") &&
      !extractedVisitTypes.includes("SICK_CHILDREN")
    ) {
      extractedVisitTypes.push("SICK_CHILDREN");
    }

    if (changeRepeating === "DO_NOT_REPEAT") {
      const createShiftPromise = createShift({
        variables: {
          input: {
            organizationId: organizationId.value,
            staffId: staff.value.id,
            visitTypes: extractedVisitTypes,
            workPlace: workPlace.value,
            status: getNfzStatus(acceptingNfz, acceptingPriv),
            day: dateStartFrom,
            startFrom: formatTimeToDate(timeFrom, dateStartFrom).toISOString(),
            endedAt: formatTimeToDate(timeTo, dateStartFrom).toISOString(),
            comment,
            overrideStaffVisitDuration: isCustomVisitDuration
              ? overrideStaffVisitDuration
              : undefined,
          },
        },
      });

      await toaster.promise(createShiftPromise, {
        success: () => {
          onCloseModal();
          return "Poprawnie utworzono zmianę";
        },
        loading: "Tworzę zmianę...",
        error: "Nie udało się utworzyć zmiany",
      });

      return;
    }

    const createShiftsBulkPromise = createShiftsBulk({
      variables: {
        input: {
          shift: {
            organizationId: organizationId.value,
            staffId: staff.value.id,
            visitTypes: extractedVisitTypes,
            workPlace: workPlace.value,
            status: getNfzStatus(acceptingNfz, acceptingPriv),
            day: dateStartFrom,
            startFrom: formatTimeToDate(timeFrom, dateStartFrom).toISOString(),
            endedAt: formatTimeToDate(timeTo, dateStartFrom).toISOString(),
            comment,
            overrideStaffVisitDuration: isCustomVisitDuration
              ? overrideStaffVisitDuration
              : undefined,
          },
          availableDays:
            changeRepeating === "WEEKDAYS"
              ? ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"]
              : availableDays,
          dateEnded: dayjs(dateEnded).format("YYYY-MM-DD"),
          dateStartFrom: dayjs(dateStartFrom).format("YYYY-MM-DD"),
        },
      },
    });

    const response = await toaster.promise(createShiftsBulkPromise, {
      success: () => {
        onCloseModal();
        return "Poprawnie utworzono zmiany";
      },
      loading: "Tworzę zmiany...",
      error: "Nie udało się utworzyć zmian",
    });

    const erroredDays = response?.data?.edmCreateShiftsBulk.erroredDays ?? [];

    if (erroredDays.length > 0) {
      const transformErroredDays = erroredDays.map((date) =>
        dayjs(date).format("D MMM"),
      );

      toaster.warning(
        `Nie udało się dodać zmiany. Pracownik ma już zaplanowane zmiany w wybranych dniach: ${transformErroredDays.join(", ")}`,
      );
    }
  };

  return {
    visitTypeOptions,
    control,
    errors,
    loading,
    handleSubmit,
    onSubmit,
    trigger,
    setValue,
    watch,
  };
};
